// Admin — anonymous site feedback list (from the home page feedback band).
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Meh, Smile, Laugh, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RATING_META = {
  4: { Icon: Laugh, label: 'Loved it', color: '#0d9488', bg: 'bg-teal-50' },
  3: { Icon: Smile, label: 'Helped', color: '#059669', bg: 'bg-emerald-50' },
  2: { Icon: Meh, label: 'Okay', color: '#d97706', bg: 'bg-amber-50' },
};

const AdminFeedback = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/feedback/admin`, { withCredentials: true });
      setData(d);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <p className="text-sm text-gray-500">Loading feedback…</p>;
  if (!data) return <p className="text-sm text-gray-400 italic">Could not load feedback.</p>;

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="admin-feedback-counts">
        {[4, 3, 2].map((r) => {
          const m = RATING_META[r];
          return (
            <div key={r} className={`rounded-xl p-3 text-center ${m.bg}`}>
              <m.Icon size={20} className="mx-auto mb-1" style={{ color: m.color }} />
              <div className="text-xl font-black text-gray-900">{data.counts?.[String(r)] ?? 0}</div>
              <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">{m.label}</div>
            </div>
          );
        })}
      </div>

      {data.items.length === 0 && (
        <p className="text-sm text-gray-400 italic" data-testid="admin-feedback-empty">
          No feedback yet — the band is live at the bottom of the home page.
        </p>
      )}

      <div className="space-y-2.5 max-h-[420px] overflow-y-auto" data-testid="admin-feedback-list">
        {data.items.map((f) => {
          const m = RATING_META[f.rating] || RATING_META[2];
          return (
            <div key={f.id} className="border border-gray-200 rounded-xl p-3 bg-gray-50 flex items-start gap-3">
              <m.Icon size={18} className="shrink-0 mt-0.5" style={{ color: m.color }} />
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-wide text-gray-500">
                  {(f.created_at || '').replace('T', ' ').slice(0, 16)} · {f.page || 'home'} · {m.label}
                </div>
                {f.message ? (
                  <p className="text-sm text-gray-800 mt-1">{f.message}</p>
                ) : (
                  <p className="text-xs text-gray-400 italic mt-1">Rating only — no message</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <button onClick={load} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold text-teal-700 hover:text-teal-900" data-testid="admin-feedback-refresh">
        <RefreshCw size={12} /> Refresh
      </button>
    </div>
  );
};

export default AdminFeedback;
